import * as React from 'react';
// Import material ui modules
import {
	ArrowForward,
	ArrowBack,
	Check,
} from '@mui/icons-material';
// Import hooks.
import {
	useFormPropertyAddressBase,
	UseFormPropertyAddressBaseResults,
	UseFormPropertyAddressBaseSharedResults,
} from '@app/hooks/view';
// Import enumerations.
import {
	AppThemeColor,
	NavigationFooterAlignment,
} from '@app/enumerations';
// Import props.
import {
	FormPopupConfirmationProps,
	FormPropertyAddressProps,
	FormProps,
	NavigationFooterProps,
	ScrollbarProps,
	StatusProps,
} from '@app/props';

interface UseFormPropertyAddressParameters {
	id: string;
	props: FormPropertyAddressProps,
}

interface UseFormPropertyAddressResults extends UseFormPropertyAddressBaseSharedResults {
	submitForm: () => void;
	applyFormProps: () => FormProps & ScrollbarProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
	applySubmissionFormPopupConfirmationProps: () => FormPopupConfirmationProps;
	applyRequestToChangeLocationFormPopupConfirmationProps: () => FormPopupConfirmationProps;
	title: string;
}

const useFormPropertyAddress = (params: UseFormPropertyAddressParameters): UseFormPropertyAddressResults => {

	// #region exported methods

	/**
	 *  Starts the submission process for the address.
	 **/
	const submitForm = (): void => {
		submitFormBase((): boolean => {
			let proceedWithSubmission: boolean = true;
			if (requireConfirmationOnSave) {
				setOpenSaveConfirmationPopup(true);
				proceedWithSubmission = false;
			}
			return proceedWithSubmission;
		});
	}

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title,
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: false,
			statusProps: applyStatusProps(),
		};
	};

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		let isSaveRequired: boolean = doesFormRequireSave();
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Back',
			leftButtonClick: params.props.onBack,
			leftButtonIcon: ArrowBack,
			rightButtonDisabled: false,
			rightButtonLabel: isSaveRequired ? 'Save' : 'Continue',
			rightButtonClick: isSaveRequired ? submitForm : params.props.onForward,
			rightButtonIcon: isSaveRequired ? Check : ArrowForward,
			alignment: NavigationFooterAlignment.SPACE_BETWEEN,
		};
	};

	/**
	 * Returns props for the submission confirmation popup form. 
	 **/
	const applySubmissionFormPopupConfirmationProps = (): FormPopupConfirmationProps => {
		return {
			isOpen: openSaveConfirmationPopup,
			onClose: cancelConfirmSubmission,
			onConfirm: confirmSubmission,
			message: params.props.confirmationSaveMessage ?? String.empty(),
		};
	};

	/**
	 * Returns props for the request to change location confirmation popup form. 
	 **/
	const applyRequestToChangeLocationFormPopupConfirmationProps = (): FormPopupConfirmationProps => {
		return {
			isOpen: openRequestToChangeLocationConfirmationPopup,
			onClose: cancelConfirmRequestToChangeLocation,
			onConfirm: confirmRequestToChangeLocation,
			message: params.props.confirmationSaveMessage ?? String.empty(),
		};
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Returns props for the status. 
	 **/
	const applyStatusProps = (): StatusProps | undefined => {
		let statusProps: StatusProps | undefined = undefined;
		if (showStatusMessage) {
			statusProps = {
				statusMessage: params.props.statusMessage ?? String.empty(),
				statusType: AppThemeColor.ERROR,
			}
		}

		return statusProps;
	}

	/**
	 * Confirms submission if user chooses to. 
	 **/
	const confirmSubmission = (): void => {
		setOpenSaveConfirmationPopup(false);
		formPropertyAddressBase.subformPropertyAddress.verifyAddress();
	}

	/**
	 * Confirms request to change location is user chooses to. 
	 **/
	const confirmRequestToChangeLocation = (): void => {
		setOpenRequestToChangeLocationConfirmationPopup(false);
		if (params.props.onRequestToChangeLocation !== undefined) {
			params.props.onRequestToChangeLocation();
		}
	}

	/**
	 * Cancels the confirmation prompt for submission. 
	 **/
	const cancelConfirmSubmission = (): void => {
		setOpenSaveConfirmationPopup(false);
	}

	/**
	 * Cancels the confirmation prompt for request to change location. 
	 **/
	const cancelConfirmRequestToChangeLocation = (): void => {
		setOpenRequestToChangeLocationConfirmationPopup(false);
	}

	/**
	 * Makes request call to change location. 
	 **/
	const requestToChangeLocation = (): void => {
		// Make sure the confirmation popup appears if it's required.
		if (params.props.requireConfirmationOnSave) {
			setOpenRequestToChangeLocationConfirmationPopup(true);
		}
		else if (params.props.onRequestToChangeLocation !== undefined) {
			params.props.onRequestToChangeLocation();
		}
	}

	/**
	 * Checks to see if the form has changes. 
	 **/
	const doesFormHaveChanges = (): boolean => {
		return formPropertyAddressBase.subformPropertyAddress.doInputsHaveChanges();
	}

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	/**
	 * Checks to see if the form requires saving. 
	 **/
	const doesFormRequireSave = (): boolean => {
		return params.props.allowEdits
			&& (doesFormHaveChanges()
				|| params.props.isReviewRequired);
	}

	// #endregion private methods


	// #region variables

	let requireConfirmationOnSave: boolean = params.props.requireConfirmationOnSave ?? false;

	// Setup the base form.
	const {
		submitForm: submitFormBase,
		checkFormValidation,
		...formPropertyAddressBase
	}: UseFormPropertyAddressBaseResults = useFormPropertyAddressBase({
		id: generateIdForForm(),
		isFormDisabled: !params.props.allowEdits,
		props: params.props,
		requestToChangeLocation: params.props.onRequestToChangeLocation !== undefined ? requestToChangeLocation : undefined,
	});

	// Title string value. 
	const title: string = React.useMemo(() => {
		return formPropertyAddressBase.subformPropertyAddress.isCommercial
			? 'Commercial Property Address'
			: 'Property Address';
	}, [formPropertyAddressBase.subformPropertyAddress.isCommercial]);

	const [openSaveConfirmationPopup, setOpenSaveConfirmationPopup] = React.useState<boolean>(false)
	const [openRequestToChangeLocationConfirmationPopup, setOpenRequestToChangeLocationConfirmationPopup] = React.useState<boolean>(false)
	const [showStatusMessage, setShowStatusMessage] = React.useState<boolean>(false);

	// #endregion variables

	// #region life-cycle methods.

	// Called when updates are made the props related to the status of the form.
	React.useEffect(() => {
		// Determine if the status message should be rendered.
		if (params.props.isReviewRequired
			&& !String.isNullOrWhiteSpace(params.props.statusMessage)
			&& params.props.allowEdits
			&& checkFormValidation(false)) {
			setShowStatusMessage((currentShowStatusMessage: boolean) => {
				return true;
			});
		}
		else {
			setShowStatusMessage((currentShowStatusMessage: boolean) => {
				return false;
			});
		}
	}, [params.props.isReviewRequired, params.props.statusMessage, params.props.allowEdits]);

	// #endregion life-cycle methods.

	return {
		...formPropertyAddressBase,
		submitForm,
		applyFormProps,
		applyNavigationFooterProps,
		applySubmissionFormPopupConfirmationProps,
		applyRequestToChangeLocationFormPopupConfirmationProps,
		title,
	};
}

// Export parameters.
export {
	UseFormPropertyAddressParameters,
}

// Export results.
export {
	UseFormPropertyAddressResults,
}

export default useFormPropertyAddress;